import { UseFormReturn } from 'react-hook-form';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';
import {
  GuardianFormValues,
  getRelationOptions,
} from './guardian-demographics-form';
import {
  AssessmentCard,
  ControlledDateField,
  ControlledMaskedField,
  ControlledSelectField,
  ControlledTextField,
} from '../shared';
import { rules } from './rules';
import { stateOptions } from '../../../helpers/states';
import { Icon } from '../../icon/icon';
import { selectPatient } from '../../../redux/assessment';
import { selectLdFlags } from '../../../redux/ld-flags';
import { PatientGuardianInput } from '../../../generated/graphql';

type PropTypes = {
  form: UseFormReturn<GuardianFormValues>;
  secondaryGuardian?: PatientGuardianInput;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
};

export const GuardianDemographicsSecondary = ({
  form,
  secondaryGuardian,
  isExpanded,
  setIsExpanded,
}: PropTypes) => {
  const { control, setValue, watch } = form;
  const patient = useSelector(selectPatient);
  const { moreGuardianRelationOptions } = useSelector(selectLdFlags);
  const relationOptions = getRelationOptions(moreGuardianRelationOptions);
  const isAddressSameAsMinor = watch('isSecondaryGuardianAddressSameAsMinor');

  const removeSecondaryGuardian = () => {
    setIsExpanded(!isExpanded);
  };

  const handleSecondaryAddressSameAsPatientChange = () => {
    setValue('isSecondaryGuardianAddressSameAsMinor', !isAddressSameAsMinor);
  };

  const populateOrClearAllAddressFields = () => {
    const fieldToValueMap: Partial<Record<keyof GuardianFormValues, string>> = {
      secondaryGuardianAddressStreet1: patient.addressStreet1,
      secondaryGuardianAddressStreet2: patient.addressStreet2,
      secondaryGuardianAddressCity: patient.addressCity,
      secondaryGuardianAddressState: patient.addressState,
      secondaryGuardianAddressZipCode: patient.addressZipCode,
    };

    const updateAddressValue = (
      name: keyof GuardianFormValues,
      value: string
    ) => {
      setValue(name, isAddressSameAsMinor ? value : '', {
        shouldValidate: !!isAddressSameAsMinor,
      });
    };

    Object.entries(fieldToValueMap).forEach(
      ([key, value]: [keyof GuardianFormValues, string]) => {
        updateAddressValue(key, value);
      }
    );
  };

  useEffect(() => {
    populateOrClearAllAddressFields();
  }, [isAddressSameAsMinor]);

  return (
    <>
      <Collapse in={!isExpanded}>
        <List disablePadding>
          <ListItemButton
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Icon icon="CirclePlus" />
            </ListItemIcon>
            <ListItemText
              primary="Add secondary guardian"
              secondary="Add a secondary legal guardian if one exists."
            />
          </ListItemButton>
        </List>
      </Collapse>
      <Collapse in={isExpanded} unmountOnExit>
        <Box sx={{ py: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h5" component="h3">
              Secondary guardian
            </Typography>
            <Tooltip title="Remove">
              <IconButton onClick={removeSecondaryGuardian}>
                <Icon icon="Trash" fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body1" sx={{ pt: 1 }} color="GrayText">
            This person will be the secondary point of contact. We'll reach out
            to them if the primary contact cannot be reached.
          </Typography>
        </Box>
        <AssessmentCard>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography component="h4" variant="h6">
                Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianFirstName"
                control={control}
                defaultValue={secondaryGuardian?.firstName ?? ''}
                autoComplete="on"
                inputProps={{ maxLength: 50 }}
                label="Legal first name"
                rules={rules.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianLastName"
                control={control}
                defaultValue={secondaryGuardian?.lastName ?? ''}
                autoComplete="on"
                inputProps={{ maxLength: 50 }}
                label="Last name"
                rules={rules.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledDateField<GuardianFormValues>
                name="secondaryGuardianBirthDate"
                form={form}
                defaultValue={secondaryGuardian?.birthDate ?? null}
                label="Date of birth"
                rules={rules.birthDate}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledMaskedField<GuardianFormValues>
                name="secondaryGuardianPhone"
                control={control}
                defaultValue={secondaryGuardian?.phone ?? ''}
                autoComplete="on"
                rules={rules.phone}
                label="Phone"
                mask="(999) 999-9999"
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianEmail"
                control={control}
                defaultValue={secondaryGuardian?.email ?? ''}
                label="Email"
                rules={rules.email}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledSelectField<GuardianFormValues>
                name="secondaryGuardianRelationToPatient"
                control={control}
                defaultValue={secondaryGuardian?.relationToPatient ?? ''}
                label="Relation to patient"
                rules={rules.relationToPatient}
                options={relationOptions}
              />
            </Grid>
          </Grid>
        </AssessmentCard>
        <AssessmentCard>
          <Grid container columnSpacing={1} rowSpacing={2}>
            <Grid item xs={12}>
              <Typography component="h4" variant="h6">
                Address
              </Typography>
              <FormControlLabel
                label="Address same as patient"
                control={<Checkbox />}
                checked={isAddressSameAsMinor}
                onChange={handleSecondaryAddressSameAsPatientChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianAddressStreet1"
                control={control}
                defaultValue={
                  isAddressSameAsMinor ? patient.addressStreet1 : ''
                }
                autoComplete="on"
                inputProps={{ maxLength: 50 }}
                label="Street"
                disabled={isAddressSameAsMinor}
                rules={rules.addressStreet1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianAddressStreet2"
                control={control}
                defaultValue={
                  isAddressSameAsMinor ? patient.addressStreet2 : ''
                }
                autoComplete="on"
                inputProps={{ maxLength: 50 }}
                label="Apartment, suite, etc. (optional)"
                disabled={isAddressSameAsMinor}
              />
            </Grid>
            <Grid item xs={12}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianAddressCity"
                control={control}
                defaultValue={isAddressSameAsMinor ? patient.addressCity : ''}
                autoComplete="on"
                inputProps={{ maxLength: 50 }}
                label="City"
                disabled={isAddressSameAsMinor}
                rules={rules.addressCity}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledSelectField<GuardianFormValues>
                name="secondaryGuardianAddressState"
                control={control}
                defaultValue={isAddressSameAsMinor ? patient.addressState : ''}
                options={stateOptions}
                label="State"
                disabled={isAddressSameAsMinor}
                rules={rules.addressState}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ControlledTextField<GuardianFormValues>
                name="secondaryGuardianAddressZipCode"
                control={control}
                defaultValue={
                  isAddressSameAsMinor ? patient.addressZipCode : ''
                }
                autoComplete="on"
                inputMode="numeric"
                label="ZIP"
                disabled={isAddressSameAsMinor}
                rules={rules.addressZipCode}
              />
            </Grid>
          </Grid>
        </AssessmentCard>
      </Collapse>
    </>
  );
};
